import type { FrontendAccessPolicy, PlatformEntryContext, User } from '@/types';
import { getAuthorizedEntryRoute } from '@/utils/access';

const WORKSPACE_RESUME_KEY = 'mg_workspace_resume_v1';
const WORKSPACE_RESUME_TTL_MS = 12 * 60 * 60 * 1000;
const SAFE_WORKING_ROUTES = new Set(['/tickets', '/messages']);

type WorkspaceResumeRecord = {
  version: 1;
  userId: number;
  tenantId: string;
  host: string;
  pathname: string;
  ticketId: number | null;
  contour: string;
  savedAt: number;
};

type WorkspaceLocation = {
  pathname: string;
  search: string;
};

const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const normalizeToken = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const removeRecord = (target = storage()): void => {
  try {
    target?.removeItem(WORKSPACE_RESUME_KEY);
  } catch {
    // Session continuity must never block login or logout.
  }
};

const parsePositiveInt = (value: string | null): number | null => {
  const parsed = value ? Number(value) : NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export function rememberWorkspaceResume(
  user: User,
  location: WorkspaceLocation,
  now = Date.now(),
  target = storage(),
): void {
  if (!target || !SAFE_WORKING_ROUTES.has(location.pathname)) return;
  const params = new URLSearchParams(location.search);
  const record: WorkspaceResumeRecord = {
    version: 1,
    userId: Number(user.id),
    tenantId: normalizeToken(user.tenant_id),
    host: typeof window === 'undefined' ? '' : window.location.host.toLowerCase(),
    pathname: location.pathname,
    ticketId: parsePositiveInt(params.get('ticketId')),
    contour: normalizeToken(params.get('contour')),
    savedAt: now,
  };
  try {
    target.setItem(WORKSPACE_RESUME_KEY, JSON.stringify(record));
  } catch {
    // A private browser may deny storage; server-side feed position still works.
  }
}

export function readWorkspaceResumeRoute(
  user: User | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  entryContext: PlatformEntryContext | null | undefined,
  hostname: string,
  now = Date.now(),
  target = storage(),
): string | null {
  if (!target || !user || !entryContext) return null;
  let record: WorkspaceResumeRecord;
  try {
    record = JSON.parse(target.getItem(WORKSPACE_RESUME_KEY) ?? 'null') as WorkspaceResumeRecord;
  } catch {
    removeRecord(target);
    return null;
  }
  if (
    !record
    || record.version !== 1
    || record.userId !== Number(user.id)
    || record.tenantId !== normalizeToken(user.tenant_id)
    || record.host !== hostname.toLowerCase()
    || now - Number(record.savedAt) > WORKSPACE_RESUME_TTL_MS
    || !SAFE_WORKING_ROUTES.has(record.pathname)
  ) {
    removeRecord(target);
    return null;
  }
  const authorizedRoute = getAuthorizedEntryRoute(user, accessPolicy, record.pathname);
  if (authorizedRoute !== record.pathname) {
    removeRecord(target);
    return null;
  }
  const allowedContours = new Set(
    (entryContext.contours ?? []).map((item) => normalizeToken(item.tenant_id)).filter(Boolean),
  );
  const verticalWorkspace = entryContext.current_workspace?.kind === 'vertical';
  const verticalTenantCount = new Set(
    (entryContext.current_workspace?.tenant_ids ?? []).map(normalizeToken).filter(Boolean),
  ).size;
  const contour = verticalWorkspace && verticalTenantCount > 1
    ? ''
    : normalizeToken(record.contour || (verticalWorkspace ? '' : record.tenantId));
  if (contour && !allowedContours.has(contour)) {
    removeRecord(target);
    return null;
  }
  const params = new URLSearchParams();
  if (record.ticketId != null) params.set('ticketId', String(record.ticketId));
  if (contour) params.set('contour', contour);
  const search = params.toString();
  return `${record.pathname}${search ? `?${search}` : ''}`;
}

export function clearWorkspaceResume(): void {
  removeRecord();
}
